/**
 * 09 · The category — replaces the pending-benchmarks section with the
 * category-defining pitch: every era gets its backend; rcrt is the AI era's.
 * All comparisons are category-level (no competitor brand call-outs) and
 * qualitative — no invented metrics.
 */

const ERAS = [
  {
    year: '~2005',
    name: 'The web era',
    stack: 'LAMP · Rails',
    what: 'The database was the backend. You built pages on a relational core.',
    built: 'websites',
  },
  {
    year: '~2015',
    name: 'The interactive era',
    stack: 'Firebase · Supabase',
    what: 'Auth, data, realtime and storage behind one SDK. Backend as a service.',
    built: 'apps',
  },
  {
    year: 'now',
    name: 'The AI era',
    stack: 'rcrt',
    what: 'Memory, agents, tools and governance behind one SDK. The agent runtime.',
    built: 'digital workforces — and the apps they use',
    hot: true,
  },
];

const QUAD_POINTS = [
  { x: 14, y: 84, label: 'chatbots & GPT wrappers', sub: 'replies, then forgets' },
  { x: 30, y: 68, label: 'vibe-coding platforms', sub: 'builds, then leaves' },
  { x: 26, y: 32, label: 'workflow tools', sub: 'runs only the flow you drew' },
  { x: 54, y: 52, label: 'DIY agent frameworks', sub: 'possible — if you build it all' },
  { x: 84, y: 14, label: 'rcrt', sub: 'remembers, acts, composes', hot: true },
];

type Mark = 'yes' | 'part' | 'no';

const MATRIX: { need: string; how: string; marks: Mark[] }[] = [
  { need: 'Persistent memory', how: 'LOD engine — 4 resolution tiers, 50–200ms/turn', marks: ['yes', 'no', 'no', 'no', 'part'] },
  { need: 'Universal data model', how: 'breadcrumbs — the tag is the table', marks: ['yes', 'no', 'no', 'part', 'part'] },
  { need: 'Governed action', how: 'sandboxed tools + human approval', marks: ['yes', 'no', 'part', 'no', 'part'] },
  { need: 'Sovereign, multi-tenant runtime', how: 'your GCP project — row-level, platform → org → workspace', marks: ['yes', 'no', 'no', 'no', 'no'] },
  { need: "Composes what's missing", how: 'Studio — connector in minutes, app in a session', marks: ['yes', 'no', 'no', 'part', 'no'] },
  { need: 'Model freedom', how: '300+ models via one address', marks: ['yes', 'no', 'part', 'no', 'yes'] },
];

const COLS = ['rcrt', 'Chatbots & wrappers', 'Workflow tools', 'Vibe coding', 'DIY frameworks'];

function MarkDot({ m }: { m: Mark }) {
  return <span className={`mx-dot mx-${m}`}>{m === 'yes' ? '●' : m === 'part' ? '◐' : '○'}</span>;
}

export default function Category() {
  return (
    <section id="category">
      <div className="wrap">
        <div className="sec-head">
          <div className="sec-num">08 · The category</div>
          <div>
            <h2>
              Not a workflow. Not a chatbot. Not a wrapper.{' '}
              <span className="ph">The runtime.</span>
            </h2>
            <p className="lede">
              Workflow tools automate steps you draw. Chatbots reply and forget. Vibe-coding
              writes an app and leaves you to run it. rcrt is the layer underneath all three:
              agents with durable memory doing real work under real governance — on a runtime
              that composes whatever's missing. What Supabase was to the interactive web,
              rcrt is to the AI era.
            </p>
          </div>
        </div>

        {/* 1 · every era gets its backend */}
        <div className="era-grid">
          {ERAS.map((e) => (
            <div key={e.name} className={`era ${e.hot ? 'hot' : ''}`}>
              <div className="era-year">{e.year}</div>
              <div className="era-name">{e.name}</div>
              <div className="era-stack">{e.stack}</div>
              <p>{e.what}</p>
              <div className="era-built">
                you built <b>{e.built}</b>
              </div>
            </div>
          ))}
        </div>

        {/* 2 · positioning quadrant */}
        <div className="cat-duo">
          <div className="quad-wrap">
            <div className="quad-title">Where everything sits</div>
            <div className="quad">
              <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="quad-svg">
                <line x1="50" y1="2" x2="50" y2="98" className="quad-axis" />
                <line x1="2" y1="50" x2="98" y2="50" className="quad-axis" />
              </svg>
              <span className="quad-lab" style={{ left: '8%', top: '6%' }}>
                automates
              </span>
              <span className="quad-lab hotlab" style={{ right: '8%', top: '6%' }}>
                works
              </span>
              <span className="quad-lab" style={{ left: '8%', bottom: '6%' }}>
                talks
              </span>
              <span className="quad-lab" style={{ right: '8%', bottom: '6%' }}>
                remembers
              </span>
              {QUAD_POINTS.map((p) => (
                <div
                  key={p.label}
                  className={`quad-pt ${p.hot ? 'hot' : ''}`}
                  style={{ left: `${p.x}%`, top: `${p.y}%` }}
                >
                  <i />
                  <span className="qp-label">
                    {p.label}
                    <em>{p.sub}</em>
                  </span>
                </div>
              ))}
            </div>
            <div className="quad-axes-note">
              → durable memory &nbsp;·&nbsp; ↑ governed action
            </div>
          </div>

          {/* 3 · capability matrix */}
          <div className="matrix-wrap">
            <div className="quad-title">What an AI-era product needs</div>
            <div className="matrix-scroll">
              <div className="matrix">
                <div className="mx-row mx-head">
                  <span className="mx-need" />
                  {COLS.map((c) => (
                    <span key={c} className={`mx-col ${c === 'rcrt' ? 'hot' : ''}`}>
                      {c}
                    </span>
                  ))}
                </div>
                {MATRIX.map((r) => (
                  <div key={r.need} className="mx-row">
                    <span className="mx-need">
                      {r.need}
                      <em>{r.how}</em>
                    </span>
                    {r.marks.map((m, i) => (
                      <span key={i} className="mx-cell">
                        <MarkDot m={m} />
                      </span>
                    ))}
                  </div>
                ))}
              </div>
            </div>
            <div className="mx-legend">
              <span>
                <span className="mx-dot mx-yes">●</span> built in
              </span>
              <span>
                <span className="mx-dot mx-part">◐</span> assemble it yourself
              </span>
              <span>
                <span className="mx-dot mx-no">○</span> not there
              </span>
            </div>
          </div>
        </div>

        {/* closer */}
        <div className="cat-closer">
          <p className="editorial">
            The database made the web. The backend-as-a-service made mobile.
            <br />
            <span className="ph">The agent runtime makes the AI era.</span>
          </p>
          <p className="cat-closer-sub">
            Workflows become things agents draw. Chat becomes one surface among many. Apps
            become things it builds. The runtime is the product.
          </p>
        </div>
      </div>
    </section>
  );
}
